import { ERROR_TYPES } from './constants';

/**
 * Formats error message with dynamic values
 * @param {string} errorType - The type of error
 * @param {Object} errorData - Error data from API
 * @param {Object} labels - Label translations
 * @returns {string} Formatted error message
 */
export const formatErrorMessage = (errorType, errorData, labels) => {
  const {
    remainingAttempts,
    remainingMinutes,
    remainingSeconds,
    error: apiError,
  } = errorData;
  const { errorLabels, errorText } = labels;

  switch (errorType) {
    case ERROR_TYPES.INVALID_CODE: {
      const invalidCodeLabel = errorLabels?.[ERROR_TYPES.INVALID_CODE];

      if (invalidCodeLabel && remainingAttempts !== undefined) {
        return invalidCodeLabel.replace(
          '#remainingAttempts',
          remainingAttempts,
        );
      }

      if (remainingAttempts !== undefined) {
        return remainingAttempts > 0
          ? `Invalid verification code. ${remainingAttempts} ${
              remainingAttempts === 1 ? 'attempt' : 'attempts'
            } remaining.`
          : apiError || 'Invalid verification code';
      }

      return invalidCodeLabel || apiError || errorText;
    }

    case ERROR_TYPES.CODE_EXPIRED:
      return (
        errorLabels?.[ERROR_TYPES.CODE_EXPIRED] ||
        apiError ||
        'Verification code has expired'
      );

    case ERROR_TYPES.CODE_ALREADY_USED:
      return (
        errorLabels?.[ERROR_TYPES.CODE_ALREADY_USED] ||
        apiError ||
        'Code already used'
      );

    case ERROR_TYPES.ACCOUNT_LOCKED:
    case ERROR_TYPES.ACCOUNT_LOCKED_MAX_ATTEMPTS: {
      const lockedLabel = errorLabels?.[ERROR_TYPES.ACCOUNT_LOCKED];

      if (lockedLabel && remainingMinutes !== undefined) {
        return lockedLabel.replace('#remainingMinutes', remainingMinutes);
      }

      if (apiError && remainingMinutes !== undefined) {
        return apiError.replace('#remainingMinutes', remainingMinutes);
      }

      if (remainingMinutes !== undefined) {
        return `Account temporarily locked due to too many failed attempts. Try again in ${remainingMinutes} ${
          remainingMinutes === 1 ? 'minute' : 'minutes'
        }.`;
      }

      return lockedLabel || apiError || 'Account temporarily locked';
    }

    case ERROR_TYPES.EMAIL_IN_USE:
    case ERROR_TYPES.NEW_EMAIL_IN_USE:
    case ERROR_TYPES.PERSONAL_EMAIL_IN_USE:
      return (
        errorLabels?.[errorType] ||
        errorLabels?.[ERROR_TYPES.EMAIL_IN_USE] ||
        apiError ||
        'Email already in use by another account'
      );

    case ERROR_TYPES.EMAIL_ALREADY_VERIFIED:
      return (
        errorLabels?.[ERROR_TYPES.EMAIL_ALREADY_VERIFIED] ||
        apiError ||
        'Email already verified'
      );

    case ERROR_TYPES.EXPIRED_TOKEN:
    case ERROR_TYPES.TOKEN_REQUIRED:
      return (
        errorLabels?.[ERROR_TYPES.EXPIRED_TOKEN] ||
        apiError ||
        'Session expired. Please refresh and try again'
      );

    case ERROR_TYPES.VERIFICATION_NOT_FOUND:
      return (
        errorLabels?.[ERROR_TYPES.VERIFICATION_NOT_FOUND] ||
        apiError ||
        'Verification not found'
      );

    case ERROR_TYPES.UNAUTHORIZED:
      return (
        errorLabels?.[ERROR_TYPES.UNAUTHORIZED] || apiError || 'Unauthorized'
      );

    case ERROR_TYPES.NEW_EMAIL_DIFFERENT_FROM_CURRENT_EMAIL:
      return (
        errorLabels?.[ERROR_TYPES.NEW_EMAIL_DIFFERENT_FROM_CURRENT_EMAIL] ||
        apiError ||
        'New email is different from current email'
      );

    case ERROR_TYPES.RESEND_THROTTLE:
      return (
        errorLabels?.[ERROR_TYPES.RESEND_THROTTLE]?.replace(
          '#remainingSeconds',
          remainingSeconds || 0,
        ) || apiError
      );

    default:
      return apiError || errorText;
  }
};

/**
 * Calculates remaining minutes from timestamp
 * @param {number} timestamp - Future timestamp
 * @returns {number} Remaining minutes
 */
export const calculateRemainingMinutes = timestamp => {
  if (!timestamp) return 0;
  const remaining = timestamp - Date.now();
  return Math.ceil(remaining / (60 * 1000));
};

/**
 * Builds verification data payload for API
 * @param {Object} params - Parameters for building payload
 * @returns {Object} Verification data payload
 */
export const buildVerificationPayload = ({
  brand,
  locale,
  isGuest,
  email,
  type,
  isEmailChange,
  newEmail,
  token,
  postVerificationAction,
  account,
}) => {
  const payload = {
    brand,
    locale,
    isGuest,
  };

  if (email) {
    payload.email = email;
  }

  if (type) {
    payload.type = type;
  }

  if (isEmailChange) {
    payload.newEmail = newEmail;
    payload.isEmailChange = true;
  }

  if (!isGuest && token) {
    payload.token = token;
  }

  // Only include postVerificationAction if it exists
  // The caller is responsible for only passing it for SIGN_UP type
  if (postVerificationAction) {
    payload.postVerificationAction = postVerificationAction;
  }

  if (account) {
    payload.account = account;
  }

  return payload;
};

/**
 * Builds verify code payload for API
 * @param {Object} params - Parameters for building payload
 * @returns {Object} Verify code payload
 */
export const buildVerifyCodePayload = ({
  email,
  code,
  isGuest,
  verificationId,
  token,
  account,
}) => {
  const payload = { code };

  if (email) {
    payload.email = email;
  }

  if (isGuest) {
    payload.verificationId = verificationId;
    payload.isGuest = true;
  } else if (token) {
    payload.token = token;
  }

  if (account) {
    payload.account = account;
  }

  return payload;
};
